import React, { useEffect, useState } from 'react';
import { modalStore } from 'components/layout/modals/ModalStore';
import HoverMenu from 'ats/components/hover-menu/HoverMenu';
import UserProfileAvatar from 'ats/components/user-profile-avatar/UserProfileAvatar';
import { drawerStore } from 'ats/components/drawer/DrawerStore';
import { clientStore } from './ClientStore';
import { ClientEdit } from './components/ClientEdit';
import moment from 'moment';
import AvatarImage from 'ats/components/avatar-image/AvatarImage';
import ActionMenu from 'ats/components/action-menu/ActionMenu';
import { copyTextToClipboard } from 'ats/utils/copyToClipboard';
import { ClientJobPositions } from './sections/ClientJobPositions';
import { ClientHires } from './sections/ClientHires';
import ClientContacts from './sections/ClientContacts';
import ClientInsights from './sections/ClientInsights';
import { ClientChangelog } from './sections/ClientChangelog';

const TABS = {
	JOB_POSITIONS: 0,
	HIRES: 1,
	CLIENT_CONTACTS: 2,
	INSIGHTS: 3,
	CHANGELOG: 4
};

export default function ClientProfile({ onClose, onSuccess, clientId }) {
	const [activeTab, setActiveTab] = useState(TABS.JOB_POSITIONS);
	const [data, setData] = useState(null);

	const loadData = async id => {
		const result = await clientStore.get(id);

		setData(result.data);
	};

	useEffect(() => {
		loadData(clientId);
	}, [clientId]);

	const handleTabClick = (e, tabId) => {
		e.preventDefault();
		// If already active do nothing.
		if (activeTab === tabId) {
			return;
		}
		setActiveTab(tabId);
	};

	const handleSuccess = () => {
		onSuccess && onSuccess();
		loadData(clientId);
	};

	const menuItems = [
		{
			label: 'Edit',
			onClick: () => {
				modalStore.openModal(
					{
						title: 'Edit Client',
						size: '2xl',
						position: 'center',
						saveButtonLabel: 'Save'
					},
					<ClientEdit client={data} handleSuccess={handleSuccess} />
				);
			}
		}
	];

	const linkedinUrl = data?.linkedinUrl
		? data?.linkedinUrl
		: data?.linkedin
			? `https://www.linkedin.com/company/${data?.linkedin}`
			: null;

	return (
		<div className="ClientProfile">
			<div className="drawer-header">
				<div className="client-drawer-header-details">
					<div className="is-flex gap-4">
						<div className="client-created-date">
							<span>Created: </span>
							{moment(data?.createdAt).format('ll')}
						</div>
					</div>
					<div className="is-flex gap-4">
						<HoverMenu
							position="left"
							buttonIcon={
								<img src="/ats/icons/three-dots-icon.svg" alt="Menu button" />
							}
							items={menuItems}
						/>
					</div>
				</div>

				<button
					className="drawer-close-btn button-bg-soft-small is-icon-only"
					onClick={() => (onClose ? onClose() : drawerStore.closeDrawer())}>
					<img src="/ats/icons/close-blue-icon.svg" alt="" />
				</button>
			</div>
			<div className="client-profile-header">
				<div className="client-bio-wrapper">
					<AvatarImage
						src={data?.imageUrl}
						placeholderText={data?.name?.charAt(0) || ' '}
					/>
					<div className="client-information-wrapper">
						<h3>{data?.name}</h3>
						<p>{data?.industry?.name}</p>
						<div className="client-team-info">
							<div>
								Primary Contact:{' '}
								<strong>{data?.primaryContact || 'N/A'}</strong>
							</div>
							<div>
								Account Manager:{' '}
								{data?.accountManager ? (
									<UserProfileAvatar
										name={data?.accountManager?.name}
										imageUrl={data?.accountManager?.imageUrl}
									/>
								) : (
									<span>N/A</span>
								)}
							</div>
						</div>
					</div>
				</div>
				<div className="client-right-info">
					<div className="client-open-vacancies">
						<span>Open Vacancies</span>
						<span className="number-data">{data?.openVacancies}</span>
					</div>
					<div className="social-links-wrapper">
						{linkedinUrl ? (
							<ActionMenu
								buttonIcon={
									<img src="/ats/icons/linkedin-blue-icon.svg" alt="LinkedIn" />
								}
								tooltipText={linkedinUrl}
								tooltipPosition="left"
								items={[
									{
										label: 'Copy LinkedIn URL',
										img: '/ats/icons/copy-icon.svg',
										onClick: () => copyTextToClipboard(linkedinUrl),
										successLabel: 'LinkedIn URL Copied',
										successImg: '/ats/icons/check-icon.svg',
										resetDelay: 2000
									},
									{
										label: 'Open LinkedIn URL',
										img: '/ats/icons/send-icon.svg',
										onClick: () => window.open(linkedinUrl, '_blank')
									}
								]}
							/>
						) : (
							<div className="inactive-icon">
								<img src="/ats/icons/linkedin-icon.svg" alt="LinkedIn" />
							</div>
						)}
						{data?.website ? (
							<ActionMenu
								buttonIcon={
									<img src="/ats/icons/globe-blue-icon.svg" alt="Website" />
								}
								tooltipText={data?.website}
								tooltipPosition="left"
								items={[
									{
										label: 'Copy Website URL',
										img: '/ats/icons/copy-icon.svg',
										onClick: () => copyTextToClipboard(data?.website),
										successLabel: 'Website URL Copied',
										successImg: '/ats/icons/check-icon.svg',
										resetDelay: 2000
									},
									{
										label: 'Open Website URL',
										img: '/ats/icons/send-icon.svg',
										onClick: () => window.open(data?.website, '_blank')
									}
								]}
							/>
						) : (
							<div className="inactive-icon">
								<img src="/ats/icons/globe-2-icon.svg" alt="Website" />
							</div>
						)}
					</div>
				</div>
			</div>
			<div className="client-profile-content">
				<div className="tabbed-panel">
					<div className="tabs">
						<ul>
							<li
								className={
									'tabbed-panel-option ' +
									(activeTab === TABS.JOB_POSITIONS ? 'is-active' : '')
								}>
								<a
									href="void"
									onClick={e => handleTabClick(e, TABS.JOB_POSITIONS)}>
									<span>Job Positions ({data?.jobs?.length})</span>
								</a>
							</li>
							<li
								className={
									'tabbed-panel-option ' +
									(activeTab === TABS.HIRES ? 'is-active' : '')
								}>
								<a href="void" onClick={e => handleTabClick(e, TABS.HIRES)}>
									<span>Hires ({data?.hires})</span>
								</a>
							</li>
							<li
								className={
									'tabbed-panel-option ' +
									(activeTab === TABS.CLIENT_CONTACTS ? 'is-active' : '')
								}>
								<a
									href="void"
									onClick={e => handleTabClick(e, TABS.CLIENT_CONTACTS)}>
									<span>Client Contacts</span>
								</a>
							</li>
							<li
								className={
									'tabbed-panel-option ' +
									(activeTab === TABS.INSIGHTS ? 'is-active' : '')
								}>
								<a href="void" onClick={e => handleTabClick(e, TABS.INSIGHTS)}>
									<span>Insights</span>
								</a>
							</li>
							<li
								className={
									'tabbed-panel-option ' +
									(activeTab === TABS.CHANGELOG ? 'is-active' : '')
								}>
								<a href="void" onClick={e => handleTabClick(e, TABS.CHANGELOG)}>
									<span>Log</span>
								</a>
							</li>
						</ul>
					</div>
					<div id="tabs-content-wrapper" className="tabs-content-wrapper">
						<div
							className={`box tabbed-panel-content ${activeTab === TABS.JOB_POSITIONS ? 'is-active' : ''}`}
							id="job-positions">
							<ClientJobPositions client={data} onSuccess={handleSuccess} />
						</div>
						<div
							className={`box tabbed-panel-content ${activeTab === TABS.HIRES ? 'is-active' : ''}`}
							id="hires">
							<ClientHires client={data} onSuccess={handleSuccess} />
						</div>
						<div
							className={`box tabbed-panel-content ${activeTab === TABS.CLIENT_CONTACTS ? 'is-active' : ''}`}
							id="client-contacts">
							<ClientContacts client={data} onSuccess={handleSuccess} />
						</div>
						<div
							className={`box tabbed-panel-content ${activeTab === TABS.INSIGHTS ? 'is-active' : ''}`}
							id="insights">
							<ClientInsights client={data} onSuccess={handleSuccess} />
						</div>
						<div
							className={`box tabbed-panel-content ${activeTab === TABS.CHANGELOG ? 'is-active' : ''}`}
							id="changelog">
							<ClientChangelog client={data} onSuccess={handleSuccess} />
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
